/**
 * «Da sistemare»: the shipments a carrier refused or that were cancelled, waiting
 * for someone at the bench.
 *
 * The address form is always there, with the carrier's limits written next to each
 * field: the carrier does not always name the field it dislikes, and a cancelled
 * shipment may be cancelled precisely for its address. When the carrier does name
 * fields, those are highlighted. «Salva e riprova» saves the correction and
 * then retries, in that order — if the correction is refused the retry does
 * not leave, and the refusal is the one the operator reads. «Riprova» alone
 * retries with the address as it is.
 *
 * Without an address the carrier is not chosen yet (the rule picks it from the
 * address typed in): the form shows the widest limits. A shipment the carrier
 * merged with another (to fix, with live labels) has no form and no retry: its
 * parcels travel on those labels. It offers the reprint and «Restano insieme»;
 * the other way out is deciding in WebLabeling.
 */

import { useState } from 'react';
import {
  consigneeLimits,
  MISSING_ADDRESS,
  type CarrierState,
  type Consignee,
} from '@errebi/domain';

import type { Passo } from '@errebi/sessione';

import { AnnullaSpedizione } from './cancel-shipment';
import { openLabel } from './label';

export interface VoceDaSistemare {
  readonly ordine: string;
  readonly cliente: string;
  readonly vettore: CarrierState;
}

export interface PropsDaSistemare {
  readonly voci: readonly VoceDaSistemare[];
  readonly inCorso: boolean;
  readonly fai: (percorso: string, corpo?: unknown) => () => void;
  readonly faiInOrdine: (...passi: readonly Passo[]) => () => void;
}

type Campo = keyof Consignee;

interface DefCampo {
  readonly campo: Campo;
  readonly nome: string;
  readonly limite: string;
  readonly max?: number;
}

type Limiti = ReturnType<typeof consigneeLimits>;

/** No carrier yet: the longest address any carrier takes, and any country. */
const LIMITI_PIU_LARGHI: Limiti = {
  indirizzoMax: Math.max(consigneeLimits('BRT').indirizzoMax, consigneeLimits('GLS').indirizzoMax),
  soloItalia: false,
};

/**
 * The carrier's limits on the consignee, written where the operator types. The
 * numbers that differ between carriers come from the domain, the same ones
 * `consigneeProblems` checks.
 */
function campiDi({ indirizzoMax, soloItalia }: Limiti): readonly DefCampo[] {
  return [
    { campo: 'nome', nome: 'Nome', limite: 'da 1 a 70 caratteri', max: 70 },
    {
      campo: 'indirizzo',
      nome: 'Indirizzo',
      limite: `da 6 a ${indirizzoMax} caratteri`,
      max: indirizzoMax,
    },
    { campo: 'cap', nome: 'CAP', limite: 'max 9 caratteri', max: 9 },
    { campo: 'citta', nome: 'Città', limite: 'max 35 caratteri', max: 35 },
    { campo: 'provincia', nome: 'Provincia', limite: '2 lettere, o vuota', max: 2 },
    {
      campo: 'nazione',
      nome: 'Nazione',
      limite: soloItalia ? '2 lettere, solo IT' : '2 lettere (IT, FR…)',
      max: 2,
    },
    { campo: 'telefono', nome: 'Telefono', limite: 'facoltativo' },
    { campo: 'email', nome: 'Email', limite: 'facoltativa' },
  ];
}

type Bozza = Record<Campo, string>;

function bozzaDi(d: Consignee): Bozza {
  return {
    nome: d.nome,
    indirizzo: d.indirizzo,
    cap: d.cap,
    citta: d.citta,
    provincia: d.provincia ?? '',
    nazione: d.nazione,
    telefono: d.telefono ?? '',
    email: d.email ?? '',
  };
}

/** Empty optional fields travel as `null`, as the stored consignee has them. */
function destinatarioDa(b: Bozza): Consignee {
  const facoltativo = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    nome: b.nome.trim(),
    indirizzo: b.indirizzo.trim(),
    cap: b.cap.trim(),
    citta: b.citta.trim(),
    provincia: facoltativo(b.provincia)?.toUpperCase() ?? null,
    nazione: b.nazione.trim().toUpperCase(),
    telefono: facoltativo(b.telefono),
    email: facoltativo(b.email),
  };
}

type Gesti = Omit<PropsDaSistemare, 'voci'>;

/**
 * What the entry offers depends on where the carrier stands. Only a shipment
 * the carrier does not hold (`da-sistemare`) can be corrected and retried; on
 * one it already holds the server refuses a retry, so the way out is to cancel it
 * first; during the cancellation there is nothing to do but wait.
 */
function Voce({ voce, ...gesti }: { readonly voce: VoceDaSistemare } & Gesti) {
  const { ordine, cliente, vettore } = voce;
  const problema = vettore.problema;
  const unita = vettore.fase === 'da-sistemare' && vettore.etichette.some((e) => !e.annullata);

  return (
    <article className="riga riga--part">
      <div className="riga__cliente">
        {cliente} · ordine {ordine}
      </div>
      {problema && (
        <>
          <b className="riga__sku">{problema.titolo}</b>
          <p className="mod__hint" style={{ margin: '4px 0 0' }}>
            {problema.motivo}
          </p>
        </>
      )}

      {unita && <Unita voce={voce} {...gesti} />}
      {vettore.fase === 'da-sistemare' && !unita && <Correzione voce={voce} {...gesti} />}
      {vettore.fase === 'creata' && (
        <>
          <p className="mod__hint" style={{ margin: '10px 0 0' }}>
            La spedizione è già da {vettore.corriere}: se va rifatta, annullala e poi riprova.
          </p>
          <AnnullaSpedizione
            ordine={ordine}
            corriere={vettore.corriere}
            // On a created shipment every parcel has its live label.
            colli={vettore.etichette.filter((e) => !e.annullata).length}
            inCorso={gesti.inCorso}
            fai={gesti.fai}
          />
        </>
      )}
      {vettore.fase === 'annullamento' && (
        <p className="riga__conto" style={{ margin: '10px 0 0' }}>
          Annullamento in corso
        </p>
      )}
    </article>
  );
}

/** A merged shipment: its live labels can be reprinted, and a person decides it stays so. */
function Unita({ voce, inCorso, fai }: { readonly voce: VoceDaSistemare } & Gesti) {
  const { ordine, vettore } = voce;
  const [errore, setErrore] = useState<string | null>(null);
  const ristampa = (barcode: string) => () => {
    setErrore(null);
    openLabel(barcode).catch((e: Error) => setErrore(e.message));
  };

  return (
    <div style={{ marginTop: 10 }}>
      {vettore.etichette
        .filter((e) => !e.annullata)
        .map((e) => (
          <div key={e.collo} className="riga__conto">
            {e.collo} · parcelId {e.parcelId}
            <button
              className="mod"
              style={{ marginLeft: 8 }}
              aria-label={`Ristampa ${e.collo}`}
              onClick={ristampa(e.collo)}
            >
              Ristampa
            </button>
          </div>
        ))}
      {errore && (
        <p className="avviso avviso--no" style={{ margin: '8px 0 0' }}>
          {errore}
        </p>
      )}
      <div className="mods">
        <button
          type="button"
          className="btn btn--primario"
          disabled={inCorso}
          onClick={fai(`/fine-linea/${ordine}/restano-insieme`)}
        >
          Restano insieme
        </button>
      </div>
    </div>
  );
}

/** Correct the address and retry, or just retry. */
function Correzione({
  voce,
  inCorso,
  fai,
  faiInOrdine,
}: { readonly voce: VoceDaSistemare } & Gesti) {
  const { ordine, vettore } = voce;
  const sbagliati = vettore.problema?.campi ?? [];
  const [bozza, setBozza] = useState(() => bozzaDi(vettore.destinatario));
  const riprova = `/fine-linea/${ordine}/riprova`;
  const senzaIndirizzo = vettore.problema?.titolo === MISSING_ADDRESS;
  const limiti = senzaIndirizzo ? LIMITI_PIU_LARGHI : consigneeLimits(vettore.corriere);

  return (
    <form
      style={{ marginTop: 10 }}
      onSubmit={(e) => {
        e.preventDefault();
        faiInOrdine(
          { percorso: `/fine-linea/${ordine}/destinatario`, corpo: destinatarioDa(bozza) },
          { percorso: riprova },
        )();
      }}
    >
      {senzaIndirizzo && (
        <p className="mod__hint" style={{ margin: '0 0 8px' }}>
          Il corriere si sceglie dopo: BRT per l'estero.
        </p>
      )}
      {campiDi(limiti).map(({ campo, nome, limite, max }) => {
        const sbagliato = sbagliati.includes(campo);
        return (
          <label key={campo} className={sbagliato ? 'campo campo--errore' : 'campo'}>
            <span className="campo__k">
              {nome} <span className="mod__hint">· {limite}</span>
            </span>
            <input
              value={bozza[campo]}
              aria-invalid={sbagliato || undefined}
              {...(max === undefined ? {} : { maxLength: max })}
              onChange={(e) => setBozza({ ...bozza, [campo]: e.target.value })}
            />
          </label>
        );
      })}
      <div className="mods">
        <button type="submit" className="btn btn--primario" disabled={inCorso}>
          Salva e riprova
        </button>
        <button type="button" className="btn" disabled={inCorso} onClick={fai(riprova)}>
          Riprova
        </button>
      </div>
    </form>
  );
}

export function DaSistemare({ voci, ...gesti }: PropsDaSistemare) {
  return (
    <>
      {voci.map((v) => (
        <Voce key={v.ordine} voce={v} {...gesti} />
      ))}
    </>
  );
}
